import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion } from "@/components/ui/accordion";
import { SrIcon } from "@/components/ui/SrIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessGroups, useCreateAccessGroup } from "@/hooks/use-access-groups";
import { AccessGroupRow } from "./AccessGroupRow";

/**
 * Gruppene og medlemmene deres. Vet ingenting om mapper — se `FolderAccessCard`.
 *
 * `useAuth().user` og ikke `useImpersonation()`: `created_by` er et audit-felt,
 * og aktøren skal være den FAKTISK innloggede brukeren (CLAUDE.md). Hvem som
 * har lov avgjøres uansett av RLS, som også ser den ekte brukeren.
 */
export function AccessGroupsCard() {
  const { user: authUser } = useAuth();
  const { data: groups = [], isLoading } = useAccessGroups();
  const opprett = useCreateAccessGroup();
  const [nyttNavn, setNyttNavn] = useState<string | null>(null);

  async function lagNy() {
    if (!nyttNavn?.trim() || !authUser?.id) return;
    await opprett.mutateAsync({ name: nyttNavn.trim(), createdById: authUser.id });
    setNyttNavn(null);
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SrIcon name="users" className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-base">Grupper</h2>
          </div>
          <Button
            size="sm"
            onClick={() => setNyttNavn("")}
            disabled={nyttNavn !== null}
          >
            <SrIcon name="plus" className="mr-1 h-3.5 w-3.5" />
            Ny gruppe
          </Button>
        </div>

        {nyttNavn !== null && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3">
            <Input
              autoFocus
              aria-label="Navn på ny gruppe"
              placeholder="F.eks. Styret"
              value={nyttNavn}
              onChange={(e) => setNyttNavn(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void lagNy();
                if (e.key === "Escape") setNyttNavn(null);
              }}
              className="h-8 max-w-xs"
            />
            <Button
              size="sm"
              className="h-8"
              onClick={lagNy}
              disabled={!nyttNavn.trim() || opprett.isPending}
            >
              Opprett
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={() => setNyttNavn(null)}
            >
              Avbryt
            </Button>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Laster grupper …</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Ingen grupper ennå. En gruppe gir tilgang til noe rollen alene ikke
            rekker — typisk styret til styreprotokollene.
          </p>
        ) : (
          <Accordion type="single" collapsible className="rounded-md border">
            {groups.map((g) => (
              <AccessGroupRow key={g.id} group={g} />
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
